import WorkspacePostingStore from 'admin/WorkspacePostingStore'
import { action, computed, makeObservable, observable } from 'mobx'
import WorkspacePosting, {
  WorkspacePostingStatus,
} from '../../WorkspacePosting'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { extractMessageFromError } from 'utils/error-util'
import ApplicantManagementPanelViewModel from '../ApplicantManagementPanel/ApplicantManagementPanelViewModel'

export default class StaffingDetailsViewModel {
  showing = false
  workspacePosting: WorkspacePosting | null = null
  applicantsExpanded: boolean = true

  /**
   * View model for the applicant management panel.
   */
  applicantManagementPanelVM = new ApplicantManagementPanelViewModel(
    this.workspacePostingStore,
    () => this.workspacePosting,
    this.navVisible,
  )

  constructor(
    private workspacePostingStore: WorkspacePostingStore,
    private openUpdateWorkspacePostingDialog: (
      workspacePosting: WorkspacePosting,
    ) => void,
    private closeNav: () => void,
    private navVisible: boolean,
    private newAlert: AlertFactory,
    private refreshStatusFilter: () => Promise<void>,
  ) {
    makeObservable(this, {
      showing: observable,
      workspacePosting: observable,
      applicantsExpanded: observable,
      showActions: computed,
      showManageButton: computed,
      isNotComplete: computed,
      isInDraft: computed,
      show: action.bound,
      close: action.bound,
      toggleApplicantsExpanded: action.bound,
      updateDraft: action.bound,
      onWorkspacePostingUpdated: action.bound,
      publish: action.bound,
      markInReview: action.bound,
      reactivate: action.bound,
      manuallyClose: action.bound,
    })
  }

  get showActions() {
    return this.isNotComplete
  }

  get showManageButton() {
    return this.isNotComplete && !this.isInDraft
  }

  get isNotComplete() {
    return (
      this.workspacePosting?.status !== WorkspacePostingStatus.Closed &&
      this.workspacePosting?.status !== WorkspacePostingStatus.Cancelled
    )
  }

  get isInDraft() {
    return this.workspacePosting?.status === WorkspacePostingStatus.Draft
  }

  async show(workspacePosting: WorkspacePosting) {
    await this.workspacePostingStore.fetchWorkspacePosting(workspacePosting.id)
    this.workspacePosting = workspacePosting
    this.applicantsExpanded = true
    this.showing = true
    this.closeNav()
  }

  close() {
    this.showing = false
    this.workspacePosting = null
  }

  toggleApplicantsExpanded() {
    this.applicantsExpanded = !this.applicantsExpanded
  }

  /**
   * Opens update workspace posting dialog with current workspace posting.
   * @param workspacePosting
   */
  updateDraft() {
    if (!this.workspacePosting) {
      return
    }

    this.openUpdateWorkspacePostingDialog(this.workspacePosting)
  }

  /**
   * Reactivates an InReview Workspace Posting
   */
  async reactivate() {
    if (!this.workspacePosting) {
      return
    }

    await this.workspacePostingStore
      .reactivatePosting(this.workspacePosting.id)
      .then(() => {
        this.newAlert()
          .withState(AlertState.Success)
          .saying('Success', 'Workspace posting is now active.')
          .autoDismiss(2000)
          .show()

        this.onWorkspacePostingUpdated()
      })
      .catch((e) => {
        this.newAlert()
          .withState(AlertState.Error)
          .saying('Error', extractMessageFromError(e))
          .autoDismiss(2000)
          .show()
      })
  }

  /**
   * Manually close an InReview Workspace Posting
   */
  async manuallyClose() {
    if (!this.workspacePosting) {
      return
    }

    await this.workspacePostingStore
      .manuallyClose(this.workspacePosting.id)
      .then(() => {
        this.newAlert()
          .withState(AlertState.Success)
          .saying('Success', 'Workspace posting is now closed.')
          .autoDismiss(2000)
          .show()

        this.onWorkspacePostingUpdated()
      })
      .catch((e) => {
        this.newAlert()
          .withState(AlertState.Error)
          .saying('Error', extractMessageFromError(e))
          .autoDismiss(2000)
          .show()
      })
  }

  async onWorkspacePostingUpdated() {
    if (!this.workspacePosting) {
      return
    }

    // Refetch the workspace posting and refresh status filter
    await this.refreshStatusFilter()
    await this.workspacePostingStore.fetchWorkspacePosting(
      this.workspacePosting.id,
    )
  }

  async publish() {
    if (!this.workspacePosting) {
      return
    }

    await this.workspacePostingStore
      .publishWorkspacePosting(this.workspacePosting.id)
      .then(() => {
        this.newAlert()
          .withState(AlertState.Success)
          .saying('Success', 'Workspace posting published.')
          .autoDismiss(2000)
          .show()

        this.onWorkspacePostingUpdated()
      })
      .catch((e) => {
        this.newAlert()
          .withState(AlertState.Error)
          .saying('Error', extractMessageFromError(e))
          .autoDismiss(2000)
          .show()
      })
  }

  async markInReview() {
    if (!this.workspacePosting) {
      return
    }

    await this.workspacePostingStore
      .markInReview(this.workspacePosting.id)
      .then(() => {
        this.newAlert()
          .withState(AlertState.Success)
          .saying('Success', 'Workspace posting marked in review.')
          .autoDismiss(2000)
          .show()

        this.onWorkspacePostingUpdated()
      })
      .catch((e) => {
        this.newAlert()
          .withState(AlertState.Error)
          .saying('Error', extractMessageFromError(e))
          .autoDismiss(2000)
          .show()
      })
  }
}
